import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './Shows.css';

const API_BASE = process.env.REACT_APP_FRONTPAGE_API || 'http://localhost:5010/api';
const ASSET_BASE = process.env.REACT_APP_FRONTPAGE_ASSET_BASE || API_BASE.replace(/\/api\/?$/, '');

const resolveMediaUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith('/')) {
    if (path.startsWith('/images/') || path.startsWith('/video/') || path.startsWith('/assets/') || path.startsWith('/static/')) {
      return path;
    }
    return `${ASSET_BASE}${path}`;
  }
  return `${ASSET_BASE}/${path.replace(/^\/+/, '')}`;
};

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const normalizeNewsImagePaths = (paths, fallback = '') => {
  let source = paths;
  if (typeof source === 'string') {
    const trimmed = source.trim();
    if (!trimmed) {
      source = [];
    } else {
      try {
        const parsed = JSON.parse(trimmed);
        source = Array.isArray(parsed) ? parsed : trimmed.split(',');
      } catch {
        source = trimmed.split(',');
      }
    }
  }
  if (!Array.isArray(source)) {
    source = [];
  }
  const normalized = source
    .map((entry) => String(entry || '').trim())
    .filter(Boolean)
    .slice(0, 20);
  if (normalized.length > 0) {
    return Array.from(new Set(normalized));
  }
  const fallbackValue = String(fallback || '').trim();
  return fallbackValue ? [fallbackValue] : [];
};

const normalizeScale = (value, fallback = 100) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(40, Math.min(140, num));
};

const cleanNewsContent = (value) => {
  const raw = String(value || '');
  return raw
    .replace(/^\s*-{3,}\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const FALLBACK_NEWS = [
  {
    title: 'DØDSEGO Newsfeed',
    summary: 'Latest updates will appear here soon.',
    content: 'Stay tuned for announcements, releases and tour updates.',
    publishedAt: new Date().toISOString(),
  },
];

export default function News() {
  const [news, setNews] = useState([]);
  const [carouselIndexById, setCarouselIndexById] = useState({});
  const [carouselBaseFrameById, setCarouselBaseFrameById] = useState({});
  const [pressZoom, setPressZoom] = useState({ active: false, key: '', x: 50, y: 50 });

  const shiftCarousel = (key, total, direction) => {
    if (!key || total <= 1) return;
    setPressZoom((prev) => (prev.active && prev.key === `news-media-${key}`
      ? { active: false, key: '', x: 50, y: 50 }
      : prev));
    setCarouselIndexById((prev) => {
      const currentRaw = prev[key] || 0;
      const current = currentRaw < 0 ? 0 : currentRaw >= total ? total - 1 : currentRaw;
      const next = direction === 'prev'
        ? (current - 1 + total) % total
        : (current + 1) % total;
      return { ...prev, [key]: next };
    });
  };

  const handleCarouselImageLoad = (key, imageIndex, event) => {
    if (!key || imageIndex !== 0) return;
    const naturalWidth = Number(event?.currentTarget?.naturalWidth || 0);
    const naturalHeight = Number(event?.currentTarget?.naturalHeight || 0);
    if (!naturalWidth || !naturalHeight) return;
    setCarouselBaseFrameById((prev) => {
      const existing = prev[key];
      if (
        existing
        && Number(existing.width || 0) === naturalWidth
        && Number(existing.height || 0) === naturalHeight
      ) {
        return prev;
      }
      return {
        ...prev,
        [key]: {
          width: naturalWidth,
          height: naturalHeight,
        },
      };
    });
  };

  const releasePressZoom = useCallback(() => {
    setPressZoom((prev) => {
      if (!prev.active) {
        return prev;
      }
      return { ...prev, active: false };
    });
  }, []);

  const handleZoomTransitionEnd = useCallback((key, event) => {
    if (event.propertyName !== 'transform') {
      return;
    }
    setPressZoom((prev) => {
      if (prev.active || prev.key !== key) {
        return prev;
      }
      return { active: false, key: '', x: 50, y: 50 };
    });
  }, []);

  const updatePressZoom = useCallback((event, key, activate = false) => {
    const element = event.currentTarget;
    if (!element) {
      return;
    }
    if (activate) {
      const target = event.target;
      if (target instanceof Element && target.closest('.news-carousel-btn')) {
        return;
      }
    }
    const point = event.touches?.[0] || event.changedTouches?.[0] || event;
    const clientX = Number(point?.clientX);
    const clientY = Number(point?.clientY);
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
      return;
    }
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }
    const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
    setPressZoom((prev) => {
      if (!activate && (!prev.active || prev.key !== key)) {
        return prev;
      }
      return { active: true, key, x, y };
    });
  }, []);

  const getZoomHandlers = useCallback((key) => ({
    onMouseDown: (event) => updatePressZoom(event, key, true),
    onMouseMove: (event) => updatePressZoom(event, key, false),
    onMouseUp: releasePressZoom,
    onMouseLeave: releasePressZoom,
    onTouchStart: (event) => updatePressZoom(event, key, true),
    onTouchMove: (event) => updatePressZoom(event, key, false),
    onTouchEnd: releasePressZoom,
    onTouchCancel: releasePressZoom,
    onContextMenu: (event) => event.preventDefault(),
  }), [releasePressZoom, updatePressZoom]);

  useEffect(() => {
    if (!pressZoom.active) {
      return () => {};
    }
    window.addEventListener('mouseup', releasePressZoom);
    window.addEventListener('touchend', releasePressZoom);
    window.addEventListener('touchcancel', releasePressZoom);
    window.addEventListener('blur', releasePressZoom);
    return () => {
      window.removeEventListener('mouseup', releasePressZoom);
      window.removeEventListener('touchend', releasePressZoom);
      window.removeEventListener('touchcancel', releasePressZoom);
      window.removeEventListener('blur', releasePressZoom);
    };
  }, [pressZoom.active, releasePressZoom]);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API_BASE}/news`);
        const items = Array.isArray(response.data) ? response.data : [];
        setNews(items.map((item) => {
          const imagePaths = normalizeNewsImagePaths(item.imagePaths, item.imagePath);
          return {
            ...item,
            imagePaths,
            imagePath: imagePaths[0] || '',
            carouselImageScale: normalizeScale(item.carouselImageScale, 100),
          };
        }));
      } catch (error) {
        console.error('Error fetching news:', error);
        setNews([]);
      }
    };
    fetchNews();
  }, []);

  const list = news.length ? news : FALLBACK_NEWS;

  return (
    <div className="shows-background news-background">
      <div className="banner" />
      <div className="shows-wrapper">
        <div className="shows-container">
          <section className="shows-section news-section">
            <header className="shows-section__header news-section__header"></header>
            <div className="shows-stack">
              {list.map((item, index) => {
                const key = item._id || `${item.title || 'news'}-${index}`;
                const images = normalizeNewsImagePaths(item.imagePaths, item.imagePath)
                  .map((path) => resolveMediaUrl(path))
                  .filter(Boolean);
                const rawIndex = carouselIndexById[key] || 0;
                const imageIndex = images.length <= 0 ? 0 : rawIndex < 0 ? 0 : rawIndex >= images.length ? images.length - 1 : rawIndex;
                const image = images[imageIndex] || '';
                const publishedLabel = formatDate(item.publishedAt);
                const imageScale = normalizeScale(item.carouselImageScale, 100);
                const baseFrame = carouselBaseFrameById[key];
                const baseWidth = Number(baseFrame?.width || 0);
                const baseHeight = Number(baseFrame?.height || 0);
                const imageZoomKey = `news-media-${key}`;
                const cleanContent = cleanNewsContent(item.content);
                const leadCopy = item.summary || 'DØDSEGO crosses the sea to Denmark for Køben Festival, closing the Saturday night lineup on 28 March 2026.';
                return (
                  <article className="shows-card news-feature-card" key={key}>
                    {image && (
                      <div className="shows-card__media news-carousel-media news-feature-hero">
                        <div
                          className="news-carousel-frame"
                          style={{
                            width: `${Math.max(100, imageScale)}%`,
                            aspectRatio: baseWidth > 0 && baseHeight > 0 ? `${baseWidth} / ${baseHeight}` : '16 / 9',
                          }}
                        >
                          <img
                            key={`${key}-${imageIndex}`}
                            className={`news-carousel-image${pressZoom.active && pressZoom.key === imageZoomKey ? ' is-press-zoom' : ''}`}
                            src={image}
                            alt={item.title || 'News image'}
                            style={{
                              '--press-zoom-x': `${pressZoom.key === imageZoomKey ? pressZoom.x : 50}%`,
                              '--press-zoom-y': `${pressZoom.key === imageZoomKey ? pressZoom.y : 50}%`,
                            }}
                            onLoad={(event) => handleCarouselImageLoad(key, imageIndex, event)}
                            onTransitionEnd={(event) => handleZoomTransitionEnd(imageZoomKey, event)}
                            {...getZoomHandlers(imageZoomKey)}
                          />
                        </div>
                        <div className="news-feature-overlay">
                          <p className="news-feature-kicker">News</p>
                          {item.title && <h3 className="shows-card__title news-feature-title">{item.title}</h3>}
                          {(publishedLabel || item.summary) && (
                            <div className="news-card-meta-row news-feature-meta-row">
                              {publishedLabel && <p className="shows-card__meta news-card-date">{publishedLabel}</p>}
                              {item.summary && <p className="shows-card__subtitle news-card-subtitle">{item.summary}</p>}
                            </div>
                          )}
                          <p className="news-feature-lead">{leadCopy}</p>
                          {item.link && (
                            <a href={item.link} target="_blank" rel="noopener noreferrer" className="shows-card__cta news-feature-cta">
                              Tickets / Festival info
                            </a>
                          )}
                        </div>
                        {images.length > 1 && (
                          <>
                            <div className="news-carousel-controls">
                              <button
                                type="button"
                                className="news-carousel-btn news-carousel-btn--prev"
                                aria-label="Previous image"
                                onClick={() => shiftCarousel(key, images.length, 'prev')}
                              >
                                &#8592;
                              </button>
                              <button
                                type="button"
                                className="news-carousel-btn news-carousel-btn--next"
                                aria-label="Next image"
                                onClick={() => shiftCarousel(key, images.length, 'next')}
                              >
                                &#8594;
                              </button>
                            </div>
                            <span className="news-carousel-counter">{imageIndex + 1}/{images.length}</span>
                          </>
                        )}
                      </div>
                    )}
                    <div className="shows-card__body news-feature-details" style={{ textAlign: 'left' }}>
                      <h4 className="news-feature-details-title">More details</h4>
                      {cleanContent ? (
                        <p className="shows-card__description" style={{ whiteSpace: 'pre-wrap' }}>{cleanContent}</p>
                      ) : (
                        <p className="shows-card__description" style={{ whiteSpace: 'pre-wrap' }}>More event details will be announced soon.</p>
                      )}
                      {!image && item.title && <h3 className="shows-card__title">{item.title}</h3>}
                      {!image && (publishedLabel || item.summary) && (
                        <div className="news-card-meta-row">
                          {publishedLabel && <p className="shows-card__meta news-card-date">{publishedLabel}</p>}
                          {item.summary && <p className="shows-card__subtitle news-card-subtitle">{item.summary}</p>}
                        </div>
                      )}
                      {!image && item.link && (
                        <div className="shows-card__actions">
                          <a href={item.link} target="_blank" rel="noopener noreferrer" className="shows-card__cta news-feature-cta">
                            See event
                          </a>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>
      </div>
      <div className="banner" />
    </div>
  );
}
